import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type DataResidencyResult } from '@whinerp/client';
import { Icon, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Setup > where the data is held (F-146).
 *
 * A badge, not a control. The feature's own note: state plainly that the
 * region cannot be changed, rather than leaving somebody to work it out from a
 * missing button and then open a support ticket asking how.
 */
export function ResidencyPanel(): JSX.Element {
  const t = useT();
  const where = useQuery<DataResidencyResult, ApiError>({
    queryKey: ['residency'],
    queryFn: () => api.dataResidency({}),
  });

  /*
   * A skeleton means "still loading". Rendering one on ERROR is a spinner that
   * never resolves - the screen waits for it forever, and so does anything
   * waiting for the screen. Nothing is the honest answer for a panel that
   * could not load: the rest of Setup still works.
   */
  if (where.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (where.isError) return <></>;

  return (
    <Panel data-region="residency" id="residency">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('residency.title')}</SectionLabel>
        <p className="flex items-center gap-2 text-body text-ink">
          {/* A lock, because read-only is the point rather than an oversight. */}
          <Icon name="Lock" size="inline" decorative className="text-ink-meta" />
          <span data-residency-region={where.data.region}>{where.data.label}</span>
          <span className="text-meta text-ink-meta">{where.data.jurisdiction}</span>
        </p>
        {/* Said, not implied. */}
        {where.data.changeable ? null : (
          <p data-residency-fixed className="text-meta text-ink-meta">
            {t('residency.fixed')}
          </p>
        )}
      </div>
    </Panel>
  );
}
